import React from 'react';
import { Link } from 'react-router-dom';
import { Layers, ShoppingBag, Star } from 'lucide-react';
import ListingThumb from '@/components/marketplace/ListingThumb';
import { categoryLabel } from '@/config/earnings';
import { formatListingPrice } from '@/lib/marketplace';

/**
 * One site for sale. The whole card is a link to the listing, where it can be
 * previewed full size and bought.
 *
 * @param {object} props
 * @param {object} props.listing   public listing from /api/marketplace/listings
 * @param {{symbol:string, locale:string}} [props.pricing] how to format `listing.price`
 */
export default function ListingCard({ listing, pricing }) {
  const price = listing.price ?? listing.price_usd;
  const fmt = listing.price != null ? pricing : { symbol: '$', locale: 'en-US' };

  return (
    <Link
      to={`/marketplace/${listing.id}`}
      className="group block rounded-2xl border border-zinc-800 bg-zinc-900 overflow-hidden transition-all hover:border-zinc-700 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(0,0,0,0.35)] focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-500"
    >
      <div className="relative">
        <ListingThumb listingId={listing.id} title={listing.title} iconSvg={listing.icon_svg} />
        {listing.featured && (
          <span className="absolute top-2 left-2 inline-flex items-center gap-1 rounded-full bg-orange-500 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-ink">
            <Star className="w-3 h-3" /> Featured
          </span>
        )}
        <span className="absolute bottom-2 right-2 rounded-full bg-zinc-950/85 px-2.5 py-1 text-sm font-bold text-white backdrop-blur ring-1 ring-white/10">
          {formatListingPrice(price, fmt)}
        </span>
      </div>
      <div className="p-4">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-orange-400">{categoryLabel(listing.category)}</p>
        <h3 className="mt-1 text-white font-semibold leading-snug line-clamp-1 group-hover:text-orange-200 transition-colors">{listing.title}</h3>
        {listing.description && (
          <p className="mt-1 text-sm text-zinc-500 line-clamp-2 leading-relaxed">{listing.description}</p>
        )}
        <div className="mt-3 flex items-center justify-between gap-2 text-xs text-zinc-500">
          <span className="truncate">by {listing.seller?.name || 'a creator'}</span>
          <span className="flex items-center gap-3 shrink-0">
            <span className="inline-flex items-center gap-1"><Layers className="w-3.5 h-3.5" />{listing.page_count || 1}</span>
            {listing.sales_count > 0 && (
              <span className="inline-flex items-center gap-1"><ShoppingBag className="w-3.5 h-3.5" />{listing.sales_count}</span>
            )}
          </span>
        </div>
      </div>
    </Link>
  );
}
